/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Microphone } from '@carbon/react/icons';
import { InlineNotification } from 'carbon-components-react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppSelector } from '../../../../../store';
import { selectIsGenesysAudioConnectorInCpd } from '../../../../ia/store/features/selectors';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { ChannelContentProps } from '../../../util/types';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { getSttLearnLink, links } from '../links';
import { SpeechToText as messages } from './messages';
import { SpeechToText } from './SpeechToText';
import { SpeechToTextCpd } from './SpeechToTextCpd';

import './SpeechToText.scss';

export const SpeechToTextTab = ({ readOnly }: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();
  const isGenesysAudioConnectorEnabledInCpd = useAppSelector(
    selectIsGenesysAudioConnectorInCpd
  );

  //NOTE CP4D doc links slightly different - these are for other
  //   offerings so cannot use `docs/?xyz` syntax for which other assistant code switches form automatically
  const sttLearnLink = getSttLearnLink();

  return (
    <>
      {!isGenesysAudioConnectorEnabledInCpd && (
        <div className="TabContent SpeechToText__deprecation-notification">
          <InlineNotification
            title={t(messages.sttDeprecationTitle)}
            subtitle={
              <FormattedMessageWithLink
                url={links.learnMoreDeprecatedSttModels}
                {...messages.sttDeprecationText}
              />
            }
            kind="info"
            iconDescription="close notification"
          />
        </div>
      )}

      <TabContent
        ImageComponent={() => <Microphone size={32} />}
        title={<FormattedMessage {...messages.title} />}>
        <div className="Phone__body-content TabContent__body-content">
          <div className="Phone__body-content-width TabContent__subtitle TabContent__link-larger-text">
            <FormattedMessageWithLink
              url={sttLearnLink}
              {...messages.description}
            />
          </div>
          <div className="TabContent__spacer16" />
          {isGenesysAudioConnectorEnabledInCpd ? (
            <SpeechToTextCpd readOnly={readOnly} size="lg" />
          ) : (
            <SpeechToText readOnly={readOnly} size="lg" />
          )}
        </div>
      </TabContent>
    </>
  );
};
